import { forwardRef, useMemo } from "react";
import FullCalendar from "@fullcalendar/react"; // must go before plugins
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction"; // needed for dateClick
import "assets/css/ServiceScheduler.css";
import { endOfWeek, isWithinInterval, startOfWeek } from "date-fns";
import EnhancedEventCard from "views/admin/scheduler/components/EnhancedEventCard";

// Default event card
const DefaultEventCard = (data) => {
  return <EnhancedEventCard event={data?.event} timeText={data?.timeText} />;
};

const EventCalendar = forwardRef(function (props, ref) {
  const { calendarData, initialDate, scrollToNow = true, onViewMount, currentView = "timeGridWeek", eventContent, ...others} = props;

  // Use custom eventContent if provided, otherwise use default
  const EventCard = eventContent || DefaultEventCard;

  // Get current time for scroll position (uses device/browser time)
  const getCurrentScrollTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  // * Generate weekly event
  const generateWeeklyEvents = (event) => {
    const events = [];
    let currentStart = new Date(event.start);
    let currentEnd = new Date(event.end);

    const repeatUntil = new Date();
    repeatUntil.setMonth(repeatUntil.getMonth() + 6);

    while (currentStart <= repeatUntil) {
      events.push({
        ...event,
        start: new Date(currentStart), // clone
        end: new Date(currentEnd), // clone
      });

      // Move both forward by 7 days
      currentStart.setDate(currentStart.getDate() + 7);
      currentEnd.setDate(currentEnd.getDate() + 7);
    }
    return events;
  };

  // Transform events based on schedule type
  const processedEvents = useMemo(() => {
    return (
      calendarData?.flatMap((event) => {
        if (event?.origin?.schedule === "weekly") {
          return generateWeeklyEvents(event);
        }

        if (event?.origin?.schedule === "once") {
          const startOfCurrentWeek = startOfWeek(initialDate, {
            weekStartsOn: 1,
          });
          const endOfCurrentWeek = endOfWeek(initialDate, { weekStartsOn: 1 });

          const eventStart = new Date(event.start);
          return event.start &&
            isWithinInterval(eventStart, {
              start: startOfCurrentWeek,
              end: endOfCurrentWeek,
            })
            ? [event] // return as array to keep flatMap happy
            : [];
        }

        return event;
      }) ?? []
    );
  }, [calendarData, initialDate]);

  return (
    <FullCalendar
      ref={ref}
      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
      initialView={currentView}
      height="calc(100vh - 280px)"
      expandRows={false}
      events={processedEvents}
      initialDate={initialDate}
      headerToolbar={false}
      titleFormat={{ year: "numeric", month: "long", day: "numeric" }}
      allDaySlot={false}
      eventContent={EventCard}
      editable={false}
      selectable={true}
      eventMinHeight={currentView === "dayGridMonth" ? 80 : 130}
      firstDay={0}
      dayHeaderFormat={{ weekday: "short" }}
      dayHeaderContent={(data) => {
        // Month view headers name weekdays only; dates live in the day cells.
        const showDate = !data?.view?.type?.startsWith("dayGridMonth");
        return (
          <>
            <span className="inline-block min-h-8 content-center pr-2 text-sm font-normal text-fg-muted">
              {data?.text}
            </span>
            {showDate && (
              <span
                className={`inline-flex items-center justify-center text-sm font-bold ${
                  data?.isToday
                    ? "h-6 w-6 rounded-full bg-primary text-primary-fg"
                    : "text-fg"
                }`}
              >
                {data?.date?.getDate()}
              </span>
            )}
          </>
        );
      }}
      slotLabelFormat={{
        hour: "2-digit",
        minute: "2-digit",
        omitZeroMinute: false,
        meridiem: "uppercase",
      }}
      slotLabelClassNames="text-xs font-semibold text-fg-muted"
      slotDuration="00:30:00"
      slotMinTime="00:00:00"
      slotMaxTime="24:00:00"
      nowIndicator={true}
      scrollTime={getCurrentScrollTime()}
      scrollTimeReset={false}
      now={() => new Date()}
      viewDidMount={(info) => {
        // Notify parent component about the view (for title updates)
        if (onViewMount) {
          onViewMount(info);
        }

        // Auto-scroll to current time - Fantastical-style UX
        if (scrollToNow) {
          const now = new Date();
          const viewDate = info.view.calendar.getDate();
          const isToday = viewDate.toDateString() === now.toDateString();

          // Only auto-scroll for time-based views when viewing today
          const isTimeGridView = info.view.type.includes('timeGrid');

          if (isToday && isTimeGridView) {
            // Shared flag to prevent multiple scroll attempts
            let hasScrolled = false;

            const scrollToCurrentTime = () => {
              // Skip if we've already successfully scrolled
              if (hasScrolled) return;

              try {
                const hours = now.getHours();
                const minutes = now.getMinutes();

                // Detect actual pixels per hour from the calendar
                let pixelsPerHour = 48; // default

                // Try to measure actual slot height
                const slotElement = document.querySelector('.fc-timegrid-slot');
                if (slotElement) {
                  const slotHeight = slotElement.offsetHeight;
                  // Slots are typically 30 minutes, so double it for hourly rate
                  pixelsPerHour = slotHeight * 2;
                }

                const scrollTop = (hours * pixelsPerHour) + ((minutes / 60) * pixelsPerHour);

                // Offset by 1.5 hours for better context
                const offsetScrollTop = Math.max(0, scrollTop - (pixelsPerHour * 1.5));

                // Try multiple selectors - FullCalendar's structure varies by version
                const selectors = [
                  '.fc-scroller-liquid-absolute',
                  '.fc-timegrid-body > .fc-scroller',
                  '.fc-scroller',
                  '.fc-view-harness .fc-scroller',
                  '.fc-time-grid-container .fc-scroller',
                ];

                for (const selector of selectors) {
                  if (hasScrolled) break;

                  const elements = document.querySelectorAll(selector);

                  elements.forEach((element) => {
                    if (hasScrolled) return;

                    // Check if element is scrollable
                    const style = window.getComputedStyle(element);
                    const hasOverflow = style.overflowY === 'scroll' || style.overflowY === 'auto' || style.overflow === 'auto';

                    // Try to scroll it
                    if (hasOverflow || element.scrollHeight > 0) {
                      element.scrollTop = offsetScrollTop;

                      // Verify it actually scrolled
                      if (element.scrollTop > 0) {
                        hasScrolled = true;
                      }
                    }
                  });
                }
              } catch (error) {
                console.warn('Auto-scroll error:', error);
              }
            };

            // Multiple attempts with longer delays for FullCalendar to fully render
            // Once hasScrolled = true, subsequent calls will return early
            setTimeout(scrollToCurrentTime, 300);
            setTimeout(scrollToCurrentTime, 800);
            setTimeout(scrollToCurrentTime, 1500);
          }
        }
      }}
      {...others}
    />
  );
});

export default EventCalendar;
